import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { CATEGORY_NAMES, TABLE_POSITIONS, TABLE_COORDS, parsePositionActions, resolveHeroSeat, resolveVillainSeats } from "../lib/constants";
import { PlayingCard, PositionNode } from "../components/shared";
import { Header } from "../components/Header";
import { Loader2, ChevronLeft, ChevronRight, Play, Pause, RotateCcw, Check, X } from "lucide-react";

export const HandReplayerPage = () => {
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let c = false;
    const f = async () => {
      try {
        const r = await api.get("/replayer/hands");
        if (!c) setAttempts(r.data);
      } catch {} finally { if (!c) setLoading(false); }
    };
    f();
    return () => { c = true; };
  }, []);

  if (loading) return <div className="min-h-screen bg-[#050505]"><Header /><div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" /></div></div>;

  if (selected) return <ReplayView hand={selected} onBack={() => setSelected(null)} />;

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="hand-replayer-page"><Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8"><h1 className="text-2xl sm:text-3xl font-semibold mb-2">Hand Replayer</h1><p className="text-neutral-400">Step through your previously played hands</p></div>
        {attempts.length > 0 ? (
          <div className="space-y-3">
            {attempts.map((a) => (
              <button key={a.attempt_id} onClick={() => setSelected(a)} className="w-full text-left bg-[#121212] border border-white/5 rounded-lg p-4 hover:border-white/10 transition-colors" data-testid={`replay-hand-${a.attempt_id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex gap-1">{a.hero_hand?.map(c => <PlayingCard key={c} card={c} size="sm" />)}</div>
                    <div>
                      <div className="font-medium text-sm">{a.title}</div>
                      <div className="text-xs text-neutral-500 flex items-center gap-2 mt-0.5">
                        <span>{CATEGORY_NAMES[a.category] || a.category}</span>
                        <span className={a.difficulty === "beginner" ? "text-green-400" : a.difficulty === "intermediate" ? "text-yellow-400" : "text-red-400"}>{a.difficulty}</span>
                        <span>{new Date(a.played_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${a.is_correct ? "bg-teal-500/20" : "bg-red-500/20"}`}>
                      {a.is_correct ? <Check className="w-4 h-4 text-teal-400" /> : <X className="w-4 h-4 text-red-400" />}
                    </div>
                    <ChevronRight className="w-5 h-5 text-neutral-500" />
                  </div>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 bg-[#121212] border border-white/5 rounded-lg">
            <RotateCcw className="w-12 h-12 text-neutral-500 mx-auto mb-4" />
            <p className="text-neutral-400 mb-2">No hands to replay yet</p>
            <p className="text-neutral-500 text-sm mb-4">Complete some training scenarios first</p>
            <Link to="/training" className="text-[#D4AF37] hover:underline text-sm">Start Training</Link>
          </div>
        )}
      </main>
    </div>
  );
};

const ReplayView = ({ hand, onBack }) => {
  const scenario = hand.scenario;
  const heroRaw = scenario.positions?.hero || "";
  const heroSeat = resolveHeroSeat(heroRaw);
  const villainSeats = resolveVillainSeats(scenario.positions?.villain || "");

  // Build steps: setup → each action → board streets → decision → result
  const buildSteps = useCallback(() => {
    const steps = [];

    // Step 0: Initial setup - positions only
    steps.push({
      label: "Setup",
      description: `${scenario.blinds} | ${heroRaw} with ${scenario.hero_hand.join(" ")}`,
      visibleActions: {},
      boardCards: [],
      showDecision: false,
      showResult: false,
    });

    // Steps for each action in history
    const actionAccum = {};
    (scenario.action_history || []).forEach((action, i) => {
      const parsed = parsePositionActions(scenario.action_history.slice(0, i + 1), heroSeat);
      Object.assign(actionAccum, parsed);
      steps.push({
        label: `Action ${i + 1}`,
        description: action,
        visibleActions: { ...actionAccum },
        boardCards: [],
        showDecision: false,
        showResult: false,
      });
    });

    // Board card streets
    const board = scenario.board_cards || [];
    if (board.length >= 3) {
      steps.push({
        label: "Flop",
        description: `Flop: ${board.slice(0, 3).join(" ")}`,
        visibleActions: { ...actionAccum },
        boardCards: board.slice(0, 3),
        showDecision: false,
        showResult: false,
      });
    }
    if (board.length >= 4) {
      steps.push({
        label: "Turn",
        description: `Turn: ${board[3]}`,
        visibleActions: { ...actionAccum },
        boardCards: board.slice(0, 4),
        showDecision: false,
        showResult: false,
      });
    }
    if (board.length >= 5) {
      steps.push({
        label: "River",
        description: `River: ${board[4]}`,
        visibleActions: { ...actionAccum },
        boardCards: board.slice(0, 5),
        showDecision: false,
        showResult: false,
      });
    }

    // Decision step
    const selectedOpt = scenario.options?.find(o => o.option_id === hand.selected_option_id);
    steps.push({
      label: "Your Decision",
      description: `You chose: ${selectedOpt?.text || "Unknown"}`,
      visibleActions: { ...actionAccum },
      boardCards: board,
      showDecision: true,
      showResult: false,
    });

    // Result step
    const correctOpt = scenario.options?.find(o => o.is_correct);
    steps.push({
      label: "Result",
      description: hand.is_correct ? "Correct!" : `Best play: ${correctOpt?.text || ""}`,
      visibleActions: { ...actionAccum },
      boardCards: board,
      showDecision: true,
      showResult: true,
    });

    return steps;
  }, [hand, scenario, heroRaw, heroSeat]);

  const steps = buildSteps();
  const [stepIdx, setStepIdx] = useState(0);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    if (!playing) return;
    if (stepIdx >= steps.length - 1) { setPlaying(false); return; }
    const t = setTimeout(() => setStepIdx(i => i + 1), 1500);
    return () => clearTimeout(t);
  }, [playing, stepIdx, steps.length]);

  const step = steps[stepIdx];

  return (
    <div className="min-h-screen bg-[#0d0d0d]" data-testid="replay-view">
      <Header />
      <div className="bg-[#1a1a1a] border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 py-2 flex items-center gap-3 text-xs">
          <button onClick={onBack} className="text-neutral-400 hover:text-white flex items-center gap-1" data-testid="replay-back-btn"><ChevronLeft className="w-3.5 h-3.5" />Back</button>
          <span className="text-neutral-500">{scenario.title}</span>
          <span className="text-teal-400 font-semibold">Hero: {heroRaw}</span>
          <span className="ml-auto text-neutral-500">Step {stepIdx + 1}/{steps.length}</span>
        </div>
      </div>
      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Progress bar */}
        <div className="flex gap-1 mb-6">
          {steps.map((_, i) => (
            <button key={i} onClick={() => { setStepIdx(i); setPlaying(false); }} className={`h-1.5 flex-1 rounded-full transition-all ${i <= stepIdx ? "bg-[#D4AF37]" : "bg-white/10"}`} data-testid={`step-dot-${i}`} />
          ))}
        </div>

        {/* Step description */}
        <div className="bg-[#141414] border border-white/5 rounded-lg p-4 mb-6 flex items-center justify-between">
          <div>
            <div className="text-xs text-[#D4AF37] uppercase tracking-wider font-semibold mb-1">{step.label}</div>
            <div className="text-sm text-neutral-300">{step.description}</div>
          </div>
          {step.showResult && (
            <div className={`px-3 py-1.5 rounded-md text-sm font-bold ${hand.is_correct ? "bg-teal-500/20 text-teal-400" : "bg-red-500/20 text-red-400"}`}>
              {hand.is_correct ? "CORRECT" : "INCORRECT"}
            </div>
          )}
        </div>

        {/* Table */}
        <div className="bg-[#1a1a1a] rounded-xl border border-white/5 relative overflow-hidden mb-6" style={{ minHeight: "380px" }}>
          <div className="absolute inset-8 sm:inset-12 rounded-[50%] border border-neutral-700/50" style={{ background: "radial-gradient(ellipse at center, #1e2a1e 0%, #141414 100%)" }} />
          {TABLE_POSITIONS.map((pos, idx) => {
            const isHero = pos === heroSeat;
            const isVillain = villainSeats.has(pos) && !isHero;
            return (
              <PositionNode
                key={pos}
                label={isHero ? heroRaw : pos}
                stack={isHero ? `${scenario.stack_sizes?.hero}` : isVillain ? `${scenario.stack_sizes?.villain}` : ""}
                isHero={isHero}
                isVillain={isVillain}
                cards={isHero ? scenario.hero_hand : []}
                action={step.visibleActions[pos] || null}
                x={TABLE_COORDS[idx].x}
                y={TABLE_COORDS[idx].y}
              />
            );
          })}
          <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-2">
            <div className="text-xs text-neutral-400 font-mono mb-1">{scenario.blinds} | {scenario.stack_sizes?.hero}BB eff.</div>
            {step.boardCards.length > 0 ? (
              <div className="flex gap-1">
                {step.boardCards.map((card) => (
                  <div key={card} className="animate-card-slide"><PlayingCard card={card} size="board" /></div>
                ))}
              </div>
            ) : (
              <div className="text-xs text-neutral-500 bg-neutral-800/50 px-3 py-1 rounded">PREFLOP</div>
            )}
          </div>
        </div>

        {/* Show decision & result panels */}
        {step.showDecision && (
          <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {scenario.options?.map(opt => {
              const isSelected = opt.option_id === hand.selected_option_id;
              const isCorrect = opt.is_correct;
              let borderClass = "border-neutral-800 bg-[#141414]";
              if (step.showResult) {
                if (isCorrect) borderClass = "border-teal-400 bg-teal-500/10";
                else if (isSelected && !isCorrect) borderClass = "border-red-500 bg-red-500/10";
              } else if (isSelected) {
                borderClass = "border-[#D4AF37] bg-[#D4AF37]/10";
              }
              return (
                <div key={opt.option_id} className={`p-4 rounded-lg border-2 ${borderClass} transition-all`}>
                  <span className="text-sm font-medium flex items-center gap-2">
                    {step.showResult && isCorrect && <Check className="w-4 h-4 text-teal-400" />}
                    {step.showResult && isSelected && !isCorrect && <X className="w-4 h-4 text-red-400" />}
                    {isSelected && !step.showResult && <span className="w-2 h-2 rounded-full bg-[#D4AF37]" />}
                    {opt.text}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {step.showResult && (
          <div className="bg-[#141414] border border-white/5 rounded-lg p-5 mb-6">
            <div className="text-xs text-neutral-500 uppercase tracking-wider mb-2 font-semibold">Explanation</div>
            <div className="text-sm text-neutral-300 leading-relaxed">{scenario.explanation}</div>
          </div>
        )}

        {/* Controls */}
        <div className="flex items-center justify-center gap-3 py-4">
          <button onClick={() => { setStepIdx(0); setPlaying(false); }} disabled={stepIdx === 0} className="p-2 text-neutral-400 hover:text-white disabled:opacity-30 transition-colors" data-testid="replay-restart-btn"><RotateCcw className="w-5 h-5" /></button>
          <button onClick={() => { setStepIdx(i => Math.max(0, i - 1)); setPlaying(false); }} disabled={stepIdx === 0} className="p-2 text-neutral-400 hover:text-white disabled:opacity-30 transition-colors" data-testid="replay-prev-btn"><ChevronLeft className="w-5 h-5" /></button>
          <button onClick={() => setPlaying(p => !p)} className="w-12 h-12 rounded-full bg-[#D4AF37] text-black flex items-center justify-center hover:bg-[#B3922A] transition-colors" data-testid="replay-play-btn">
            {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
          </button>
          <button onClick={() => { setStepIdx(i => Math.min(steps.length - 1, i + 1)); setPlaying(false); }} disabled={stepIdx === steps.length - 1} className="p-2 text-neutral-400 hover:text-white disabled:opacity-30 transition-colors" data-testid="replay-next-btn"><ChevronRight className="w-5 h-5" /></button>
        </div>
      </main>
    </div>
  );
};
